import {
  Component,
  OnInit,
  ViewChild,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  OnChanges
} from '@angular/core';
import { SxCalendarComponent } from '@core/components/sx-calendar/calendar';
import { DatePeriodType } from '@enums';
import dayjs from 'dayjs';
import { MatMenuTrigger } from '@angular/material/menu';
import { String } from 'typescript-string-operations';
import { formatDate } from '@angular/common';
import { CustomCalendarHeaderComponent } from '@core/components/sx-calendar/custom-calendar-header';

@Component({
    selector: 'app-date-filter',
    templateUrl: './date-filter.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class DateFilterComponent implements OnInit, OnChanges {
  @Output()
  value: Array<Date>;

  @Input()
  mode: DatePeriodType;

  @Input()
  fromDate: Date;

  @Input()
  toDate: Date;

  @Input()
  minDate?: Date;

  @Input()
  maxDate?: Date;

  @Input()
  disabled: boolean;

  @Input()
  showCalendarDrop: boolean;

  @Input()
  showCalendarIcon: boolean;

  @Input()
  allowClear = false;

  @Input()
  disableMonthMovement = false;

  /* -- Un-used as input
  @Input()
  label: string;
  */

  @Output()
  datesChanged = new EventEmitter<Array<Date>>();

  @ViewChild('fromCalendar', { static: true })
  fromCalendar: SxCalendarComponent<Date>;

  @ViewChild('toCalendar', { static: true })
  toCalendar: SxCalendarComponent<Date>;

  @ViewChild('menuButton')
  calenderDrop: MatMenuTrigger;

  headerFrom = CustomCalendarHeaderComponent;
  headerTo = CustomCalendarHeaderComponent;

  hasChanged = false;
  selectedPeriod = ""

  firstStartSet = false;
  firstEndSet = false;

  constructor() {}

  ngOnInit() {
    this.fromCalendar.disableMonthView = this.disableMonthMovement;
    this.toCalendar.disableMonthView = this.disableMonthMovement;

    if (this.mode === DatePeriodType.Month) {
      this.fromCalendar.startView = 'year';
      this.toCalendar.startView = 'year';
    } else {
      this.fromCalendar.startView = 'month';
      this.toCalendar.startView = 'month';
    }

    if (this.minDate) {
      this.fromCalendar.startAt = this.minDate;
      this.fromCalendar.minDate = this.minDate;
      this.toCalendar.maxDate = this.minDate;
    }

    if (this.maxDate) {
      this.fromCalendar.maxDate = this.maxDate;
      this.toCalendar.maxDate = this.maxDate;
      this.toCalendar.startAt = this.maxDate;
    }

    if (this.fromDate) {
      this.fromCalendar.startAt = this.fromDate;
      this.fromCalendar.selected = this.fromDate;
    }
    if (this.toDate) {
      this.toCalendar.startAt = this.toDate;
      this.toCalendar.selected = this.toDate;
    }

    this.setLabel(this.hasChanged);
  }

  ngOnChanges() {
    if (!this.firstStartSet && (this.fromDate || this.minDate)) {
      this.fromCalendar.startAt = this.fromDate ? this.fromDate : this.minDate;
      this.firstStartSet = true;
    }

    if (!this.firstEndSet && (this.toDate || this.maxDate)) {
      this.toCalendar.startAt = this.toDate ? this.toDate : this.maxDate;
      this.firstEndSet = true;
    }

    if (this.fromDate !== this.fromCalendar.selected) {
      this.fromCalendar.selected = this.fromDate;
    }

    if (this.toDate !== this.toCalendar.selected) {
      this.toCalendar.selected = this.toDate;
    }

    if (this.minDate !== this.fromCalendar.minDate) {
      this.fromCalendar.startAt = this.minDate;
      this.fromCalendar.minDate = this.minDate;
      this.toCalendar.maxDate = this.minDate;
    }

    if (this.maxDate !== this.fromCalendar.maxDate) {
      this.fromCalendar.maxDate = this.maxDate;
      this.toCalendar.maxDate = this.maxDate;
      this.toCalendar.startAt = this.maxDate;
    }

    this.toCalendar.refreshView();
    this.fromCalendar.refreshView();

    this.setLabel(this.hasChanged);
  }

  onMenuOpen = event => {
    this.fromCalendar.selected = this.fromDate;
    this.toCalendar.selected = this.toDate;

    switch (this.mode) {
      case DatePeriodType.Month:
        this.fromCalendar.selected = dayjs(this.fromCalendar.selected)
          .startOf('m')
          .startOf('d')
          .toDate();
        this.toCalendar.selected = dayjs(this.toCalendar.selected)
          .endOf('m')
          .endOf('d')
          .toDate();
        break;
      case DatePeriodType.Day:
        break;
      case DatePeriodType.Hour:
        break;
    }

    if (this.fromCalendar.selected) {
      this.setFromCalendarDate(this.fromCalendar.selected);
    }
    if (this.toCalendar.selected) {
      this.setToCalendarDate(this.toCalendar.selected);
    }

    if (!this.fromDate && !this.toDate) {
      this.fromCalendar.maxDate = this.maxDate;
      this.toCalendar.minDate = this.minDate;
    }
    if (!this.fromDate) {
      this.fromCalendar.minDate = this.minDate;
    }
    if (!this.toDate) {
      this.toCalendar.maxDate = this.maxDate;
    }

    if (this.mode === DatePeriodType.Hour) {
      
      if (!this.minDate) {
        this.fromCalendar.minDate = dayjs()
          .subtract(4, 'years')
          .startOf('day')
          .toDate();
      }
      if (!this.maxDate) {
        this.fromCalendar.maxDate = dayjs()
          .endOf('day')
          .toDate();
      }
      if (!this.minDate) {
        this.toCalendar.minDate = dayjs()
          .subtract(4, 'years')
          .endOf('day')
          .toDate();
      }
      if (!this.maxDate) {
        this.toCalendar.maxDate = dayjs()
          .endOf('day')
          .toDate();
      }
    } else {
      if (!this.fromCalendar.minDate) {
        this.fromCalendar.minDate = dayjs()
          .subtract(4, 'years')
          .startOf('year')
          .startOf('day')
          .toDate();
      }

      if (!this.fromCalendar.maxDate) {
        this.fromCalendar.maxDate = dayjs()
          .add(1, 'years')
          .endOf('year')
          .endOf('day')
          .toDate();
      }

      if (!this.toCalendar.minDate) {
        this.toCalendar.minDate = dayjs()
          .subtract(4, 'years')
          .startOf('year')
          .startOf('day')
          .toDate();
      }
      if (!this.toCalendar.maxDate) {
        this.toCalendar.maxDate = dayjs()
          .add(1, 'years')
          .endOf('year')
          .endOf('day')
          .toDate();
      }
    }

    if (this.fromCalendar.minDate && !this.fromCalendar.selected) {
      this.setFromCalendarDate(this.fromCalendar.minDate);
    }

    if (this.toCalendar.maxDate && !this.toCalendar.selected) {
      this.setToCalendarDate(this.toCalendar.maxDate);
    }

    this.fromCalendar.refreshView();
    this.toCalendar.refreshView();

    this.hasChanged = false;
  };

  getMinDate = (selectedFrom: Date): Date => {
    const period = this.mode === DatePeriodType.Month ? 'm' : 'd';
    return dayjs(selectedFrom)
      .add(1, period)
      .startOf(period)
      .toDate();
  };

  getMaxDate = (selectedTo: Date): Date => {
    const period = this.mode === DatePeriodType.Month ? 'm' : 'd';
    return dayjs(selectedTo)
      .subtract(1, period)
      .endOf(period)
      .toDate();
  };

  onMenuClose = event => {
    if (this.hasChanged) {
      if (this.mode === DatePeriodType.Month) {
        this.fromCalendar.selected = dayjs(this.fromCalendar.selected)
          .startOf('month')
          .startOf('day')
          .toDate();
       
        this.toCalendar.selected = dayjs(this.toCalendar.selected)
          .endOf('month')
          .endOf('day')
          .toDate();
      }
      if (this.mode === DatePeriodType.Hour) {

        this.toCalendar.selected = dayjs(this.toCalendar.selected)
          .endOf('d')
          .toDate();
      }
      this.value = [this.fromCalendar.selected, this.toCalendar.selected];
      this.datesChanged.emit(this.value);
    }
  };

  clearAll = () => {
    this.fromCalendar.selected = null;
    this.toCalendar.selected = null;
    this.value = [null, null];
    this.datesChanged.emit(this.value);
    this.hasChanged = false;

    this.fromCalendar.minDate = this.minDate;
    this.fromCalendar.maxDate = this.maxDate;
    this.toCalendar.minDate = this.minDate;
    this.toCalendar.maxDate = this.maxDate;
    this.toCalendar.refreshView();
    this.fromCalendar.refreshView();
  };

  selectedFromChange = event => {
    this.toCalendar._userSelected();
    if (event < this.fromCalendar.minDate || event > this.fromCalendar.maxDate) {
      return;
    }
    this.fromCalendar.selected = event;
    this.fromCalendar.startAt = event;

    switch (this.mode) {
      case DatePeriodType.Month:
        this.fromCalendar.selected = dayjs(event)
          .startOf('month')
          .startOf('day')
          .toDate();
        this.toCalendar.minDate = dayjs(this.fromCalendar.selected)
          .endOf('month')
          .startOf('day')
          .toDate();
        break;
      case DatePeriodType.Day:
        this.toCalendar.minDate = this.fromCalendar.selected;
        break;
      case DatePeriodType.Hour:
        // this.toCalendar.minDate = this.fromCalendar.selected;
        let maxToSelectedDate = dayjs(this.fromCalendar.selected)
          .add(3, 'months')
          .add(-1, 'd')
          .endOf('day');
        let toSelectedDate = dayjs(this.toCalendar.selected);

        if (+toSelectedDate > +maxToSelectedDate) {
          this.setToCalendarDate(maxToSelectedDate.toDate());
        }
        break;
    }

    if (this.toCalendar.selected > this.toCalendar.maxDate) {
      this.setToCalendarDate(this.toCalendar.maxDate);
    }
    if (this.toCalendar.selected < this.fromCalendar.selected) {
      this.setToCalendarDate(this.fromCalendar.selected);
    }

    this.toCalendar.refreshView();
    this.fromCalendar.refreshView();
    this.hasChanged = true;
  };

  selectedToChange = event => {
    if (event < this.toCalendar.minDate || event > this.toCalendar.maxDate) {
      return;
    }

    this.toCalendar.selected = event;
    this.toCalendar.startAt = event;

    switch (this.mode) {
      case DatePeriodType.Month:
        this.toCalendar.selected = dayjs(event)
          .endOf('month')
          .endOf('day')
          .toDate();
        this.fromCalendar.maxDate = dayjs(this.toCalendar.selected)
          .startOf('month')
          .endOf('day')
          .toDate();
        break;
      case DatePeriodType.Day:
        this.fromCalendar.maxDate = this.toCalendar.selected;
        break;
      case DatePeriodType.Hour:
        // this.fromCalendar.maxDate = this.toCalendar.selected;
        let minFromSelectedDate = dayjs(this.toCalendar.selected)
          .add(-3, 'months')
          .add(1, 'd')
          .startOf('day');
        let fromSelectedDate = dayjs(this.fromCalendar.selected);

        if (+fromSelectedDate < +minFromSelectedDate) {
          this.setFromCalendarDate(minFromSelectedDate.toDate());
        }

        break;
    }
    if (this.fromCalendar.selected < this.fromCalendar.minDate) {
      this.setFromCalendarDate(this.fromCalendar.minDate);
    }
    if (this.fromCalendar.selected > this.toCalendar.selected) {
      this.setFromCalendarDate(this.toCalendar.selected);
    }

    this.toCalendar.refreshView();
    this.fromCalendar.refreshView();
    this.hasChanged = true;
  };

  setFromCalendarDate = (d: Date) => {
    this.fromCalendar.selected = d;
    this.fromCalendar.activeDate = this.fromCalendar.selected;
    this.fromCalendar.startAt = this.fromCalendar.selected;
  };

  setToCalendarDate = (d: Date) => {
    this.toCalendar.selected = d;
    this.toCalendar.activeDate = this.toCalendar.selected;
    this.toCalendar.startAt = this.toCalendar.selected;
  };

  isMonthPeriod = (): boolean => this.mode === DatePeriodType.Month;

  setLabel(_isChanged: boolean) {
    if (!_isChanged){
      this.selectedPeriod = "";
      return;
    }
    if (!this.fromCalendar.selected && !this.toCalendar.selected)
      return;
    
    let from: string;
    let to: string;
    if (this.fromCalendar.selected) {
      from = formatDate(this.fromCalendar.selected, 'd MMMM yyyy', 'sv');
      this.selectedPeriod = String.format('{0} - {1}', from);
    } else {
      from = formatDate(this.fromCalendar.minDate, 'd MMMM yyyy', 'sv');
      this.selectedPeriod = String.format('{0} - {1}', this.fromCalendar.minDate);
    }

    if (this.toCalendar.selected) {
      to = formatDate(this.toCalendar.selected, 'd MMMM yyyy', 'sv');
    } else {
      to = formatDate(this.toCalendar.maxDate, 'd MMMM yyyy', 'sv');
    }

    this.selectedPeriod = `${from}-${to}`;
  }
}
